import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { integrationsApi, type IntegrationDTO, type IntegrationKind } from '@/lib/api';
import { friendlyFromApiError } from '@/lib/errors';
import { useAuthStore } from '@/hooks/useAuthStore';

/**
 * SettingsIntegrations — C9 B.5 frontend
 *
 * SUPER_ADMIN-only console to manage infra-level secrets:
 *  - GITHUB_ACTIONS_PAT: consumed by the CT110 gh-actions-exporter via the
 *    service-token `/value` endpoint.  Rotating it here immediately affects
 *    observability (exporter cache TTL 1h).
 *  - SLACK_WEBHOOK: incoming-webhook URL for alerting.
 *
 * Every mutation (upsert, delete) requires the caller's own password + a
 * valid TOTP code (pattern D42).  Backend emits INTEGRATION_{UPDATED,DELETED}
 * audit events at CRITICAL severity.  Values are AES-256-GCM encrypted at
 * rest — this UI never shows the plaintext; the list endpoint only returns
 * a sha256-derived hint.
 *
 * Role gate: the sidebar hides the link for non-SUPER_ADMIN, and the page
 * also guards itself so a direct URL visit shows a friendly "forbidden"
 * message instead of blank 403 spam from the API.  The backend is the
 * source of truth regardless.
 */

const INTEGRATION_KINDS: { value: IntegrationKind; label: string; hint: string }[] = [
  {
    value: 'GITHUB_ACTIONS_PAT',
    label: 'GITHUB_ACTIONS_PAT',
    hint: 'Personal Access Token (ghp_… / github_pat_…) consumido por gh-actions-exporter en CT110',
  },
  {
    value: 'SLACK_WEBHOOK',
    label: 'SLACK_WEBHOOK',
    hint: 'Incoming Webhook URL (debe empezar con https://hooks.slack.com/)',
  },
];

const SLACK_WEBHOOK_RE = /^https:\/\/hooks\.slack\.com\//;

type UpsertDraft = {
  kind: IntegrationKind;
  value: string;
  password: string;
  totpCode: string;
};

type DeleteDraft = {
  kind: string;
  confirm: string;
  password: string;
  totpCode: string;
};

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function SettingsIntegrations() {
  const { staff } = useAuthStore();
  const queryClient = useQueryClient();
  const isSuperAdmin = staff?.role === 'SUPER_ADMIN';

  const integrationsQuery = useQuery({
    queryKey: ['integrations', 'list'],
    queryFn: () => integrationsApi.list(),
    enabled: isSuperAdmin,
  });

  // ─── Upsert modal state (new or update) ──────────────────────────────
  const [upsertOpen, setUpsertOpen] = useState(false);
  const [upsertDraft, setUpsertDraft] = useState<UpsertDraft>({
    kind: 'GITHUB_ACTIONS_PAT',
    value: '',
    password: '',
    totpCode: '',
  });
  const [upsertError, setUpsertError] = useState<string | null>(null);
  const [upsertMode, setUpsertMode] = useState<'create' | 'update'>('create');

  // ─── Delete modal state ───────────────────────────────────────────────
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteDraft, setDeleteDraft] = useState<DeleteDraft>({
    kind: '',
    confirm: '',
    password: '',
    totpCode: '',
  });
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Known-but-unconfigured kinds → used to populate the "add new" dropdown.
  const unconfiguredKinds = useMemo(() => {
    const configured = new Set((integrationsQuery.data ?? []).map((i) => i.kind));
    return INTEGRATION_KINDS.filter((k) => !configured.has(k.value));
  }, [integrationsQuery.data]);

  const upsertMutation = useMutation({
    mutationFn: (draft: UpsertDraft) =>
      integrationsApi.upsert(draft.kind, draft.value, draft.password, draft.totpCode),
    onSuccess: () => {
      setUpsertOpen(false);
      setUpsertDraft({
        kind: 'GITHUB_ACTIONS_PAT',
        value: '',
        password: '',
        totpCode: '',
      });
      setUpsertError(null);
      queryClient.invalidateQueries({ queryKey: ['integrations', 'list'] });
    },
    onError: (err: unknown) => {
      setUpsertError(
        friendlyFromApiError(err, 'No se pudo guardar la integración')
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (draft: DeleteDraft) =>
      integrationsApi.delete(draft.kind, draft.password, draft.totpCode),
    onSuccess: () => {
      setDeleteOpen(false);
      setDeleteDraft({ kind: '', confirm: '', password: '', totpCode: '' });
      setDeleteError(null);
      queryClient.invalidateQueries({ queryKey: ['integrations', 'list'] });
    },
    onError: (err: unknown) => {
      setDeleteError(
        friendlyFromApiError(err, 'No se pudo eliminar la integración')
      );
    },
  });

  // ─── Openers ───────────────────────────────────────────────────────────
  const openCreateModal = (kind: IntegrationKind) => {
    setUpsertMode('create');
    setUpsertDraft({ kind, value: '', password: '', totpCode: '' });
    setUpsertError(null);
    setUpsertOpen(true);
  };

  const openUpdateModal = (integration: IntegrationDTO) => {
    setUpsertMode('update');
    setUpsertDraft({
      kind: integration.kind as IntegrationKind,
      value: '',
      password: '',
      totpCode: '',
    });
    setUpsertError(null);
    setUpsertOpen(true);
  };

  const openDeleteModal = (integration: IntegrationDTO) => {
    setDeleteDraft({
      kind: integration.kind,
      confirm: '',
      password: '',
      totpCode: '',
    });
    setDeleteError(null);
    setDeleteOpen(true);
  };

  const closeUpsertModal = () => {
    if (upsertMutation.isPending) return;
    setUpsertOpen(false);
  };

  const closeDeleteModal = () => {
    if (deleteMutation.isPending) return;
    setDeleteOpen(false);
  };

  // ─── Submit handlers ──────────────────────────────────────────────────
  const handleUpsertSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const { kind, value, password, totpCode } = upsertDraft;
    if (value.trim().length === 0) {
      setUpsertError('El valor no puede estar vacío.');
      return;
    }
    if (kind === 'SLACK_WEBHOOK' && !SLACK_WEBHOOK_RE.test(value.trim())) {
      setUpsertError('El webhook debe empezar con https://hooks.slack.com/');
      return;
    }
    if (password.length < 8) {
      setUpsertError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (totpCode.length !== 6) {
      setUpsertError('El código TOTP debe tener 6 dígitos.');
      return;
    }
    setUpsertError(null);
    upsertMutation.mutate({ kind, value: value.trim(), password, totpCode });
  };

  const handleDeleteSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (deleteDraft.confirm !== 'DELETE') {
      setDeleteError('Escribe DELETE para confirmar.');
      return;
    }
    if (deleteDraft.password.length < 8) {
      setDeleteError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (deleteDraft.totpCode.length !== 6) {
      setDeleteError('El código TOTP debe tener 6 dígitos.');
      return;
    }
    setDeleteError(null);
    deleteMutation.mutate(deleteDraft);
  };

  // ─── Render ───────────────────────────────────────────────────────────
  if (!isSuperAdmin) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="p-4 rounded-lg bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800">
          <p className="font-semibold">Acceso restringido</p>
          <p className="text-sm mt-1">
            Esta página solo está disponible para SUPER_ADMIN. Si crees que deberías tener acceso,
            contacta al administrador principal.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Integraciones de infraestructura
        </h1>
        <p className="text-gray-600 dark:text-gray-300">
          Gestiona los secretos que alimentan observabilidad y alertas (GitHub Actions PAT, Slack
          webhook).
        </p>
      </div>

      {/* Scope warning */}
      <div className="mb-6 p-4 rounded-lg bg-amber-50 text-amber-800 border border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800">
        <p className="font-semibold">Cambios aquí afectan a la observability de deploy (CT110) y alertas.</p>
        <p className="text-sm mt-1">
          Solo SUPER_ADMIN puede modificar integraciones. Cada cambio requiere tu contraseña
          actual + un código TOTP válido (re-auth pattern D42) y queda registrado en el audit
          log con severidad CRITICAL.
        </p>
      </div>

      {/* Loading / error */}
      {integrationsQuery.isLoading && (
        <div className="flex items-center gap-3 text-gray-600 dark:text-gray-300">
          <div className="animate-spin h-5 w-5 border-2 border-primary-500 border-t-transparent rounded-full" />
          Cargando integraciones...
        </div>
      )}
      {integrationsQuery.isError && (
        <div className="p-3 rounded-lg bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800 text-sm">
          No se pudieron cargar las integraciones.
        </div>
      )}

      {/* Configured integrations list */}
      {!integrationsQuery.isLoading && (
        <div className="space-y-4">
          {(integrationsQuery.data ?? []).map((integration) => (
            <div
              key={integration.kind}
              className="card flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 font-mono">
                    {integration.kind}
                  </h3>
                  {integration.enabled ? (
                    <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                      activo
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                      deshabilitado
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 font-mono truncate">
                  hint: sha256:{integration.valueHint}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Última actualización: {formatDate(integration.updatedAt)} por{' '}
                  {integration.updatedBy}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => openUpdateModal(integration)}
                  className="btn-secondary"
                >
                  Actualizar
                </button>
                <button
                  type="button"
                  onClick={() => openDeleteModal(integration)}
                  className="px-4 py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  Eliminar
                </button>
              </div>
            </div>
          ))}

          {/* Add new integration card */}
          {unconfiguredKinds.length > 0 && (
            <div className="card">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Añadir nueva integración
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                Selecciona el tipo de integración a configurar.
              </p>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <select
                  value={upsertDraft.kind}
                  onChange={(e) =>
                    setUpsertDraft((d) => ({
                      ...d,
                      kind: e.target.value as IntegrationKind,
                    }))
                  }
                  className="input max-w-xs"
                >
                  {unconfiguredKinds.map((k) => (
                    <option key={k.value} value={k.value}>
                      {k.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => openCreateModal(upsertDraft.kind)}
                  className="btn-primary"
                >
                  Configurar
                </button>
              </div>
            </div>
          )}

          {(integrationsQuery.data ?? []).length === 0 && unconfiguredKinds.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No hay tipos de integración disponibles.
            </p>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          Upsert modal (create OR update)
         ═══════════════════════════════════════════════════════════════════ */}
      {upsertOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="upsert-title"
        >
          <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2
                id="upsert-title"
                className="text-lg font-semibold text-gray-900 dark:text-gray-100"
              >
                {upsertMode === 'create' ? 'Configurar' : 'Actualizar'}{' '}
                <span className="font-mono">{upsertDraft.kind}</span>
              </h2>
            </div>

            <form onSubmit={handleUpsertSubmit} className="px-5 py-4 space-y-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {INTEGRATION_KINDS.find((k) => k.value === upsertDraft.kind)?.hint}
              </p>

              <div>
                <label
                  htmlFor="upsert-value"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  {upsertDraft.kind === 'SLACK_WEBHOOK' ? 'Webhook URL' : 'Valor'}
                </label>
                <input
                  id="upsert-value"
                  type={upsertDraft.kind === 'SLACK_WEBHOOK' ? 'url' : 'password'}
                  value={upsertDraft.value}
                  onChange={(e) =>
                    setUpsertDraft((d) => ({ ...d, value: e.target.value }))
                  }
                  className="input w-full font-mono text-sm"
                  placeholder={
                    upsertDraft.kind === 'SLACK_WEBHOOK'
                      ? 'https://hooks.slack.com/services/...'
                      : 'ghp_... o github_pat_...'
                  }
                  autoComplete="off"
                  spellCheck={false}
                  required
                  autoFocus
                  disabled={upsertMutation.isPending}
                />
                {upsertMode === 'update' && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Introduce el nuevo valor completo. No hay modo "partial update".
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="upsert-password"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Tu contraseña actual
                </label>
                <input
                  id="upsert-password"
                  type="password"
                  value={upsertDraft.password}
                  onChange={(e) =>
                    setUpsertDraft((d) => ({ ...d, password: e.target.value }))
                  }
                  className="input w-full"
                  autoComplete="current-password"
                  minLength={8}
                  required
                  disabled={upsertMutation.isPending}
                />
              </div>

              <div>
                <label
                  htmlFor="upsert-totp"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Código TOTP (6 dígitos)
                </label>
                <input
                  id="upsert-totp"
                  type="text"
                  value={upsertDraft.totpCode}
                  onChange={(e) =>
                    setUpsertDraft((d) => ({
                      ...d,
                      totpCode: e.target.value.replace(/\D/g, '').slice(0, 6),
                    }))
                  }
                  className="input w-full text-center text-2xl tracking-widest"
                  placeholder="000000"
                  maxLength={6}
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  autoComplete="one-time-code"
                  required
                  disabled={upsertMutation.isPending}
                />
              </div>

              {upsertError && (
                <div className="p-3 rounded-lg bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800 text-sm">
                  {upsertError}
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeUpsertModal}
                  disabled={upsertMutation.isPending}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={
                    upsertMutation.isPending ||
                    upsertDraft.value.trim().length === 0 ||
                    upsertDraft.password.length < 8 ||
                    upsertDraft.totpCode.length !== 6
                  }
                  className="btn-primary"
                >
                  {upsertMutation.isPending ? 'Guardando...' : 'Guardar integración'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          Delete modal (destructive — requires typed DELETE + password + TOTP)
         ═══════════════════════════════════════════════════════════════════ */}
      {deleteOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-title"
        >
          <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2
                id="delete-title"
                className="text-lg font-semibold text-gray-900 dark:text-gray-100"
              >
                Eliminar <span className="font-mono">{deleteDraft.kind}</span>
              </h2>
            </div>

            <form onSubmit={handleDeleteSubmit} className="px-5 py-4 space-y-4">
              <div className="p-3 rounded-lg bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800 text-sm">
                <p className="font-semibold">Esto es destructivo.</p>
                <p className="mt-1">
                  Deshabilitará la observability y/o alertas relacionadas con{' '}
                  <span className="font-mono">{deleteDraft.kind}</span>. Para confirmar, escribe
                  <span className="font-mono font-semibold"> DELETE</span> abajo.
                </p>
              </div>

              <div>
                <label
                  htmlFor="delete-confirm"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Confirmación
                </label>
                <input
                  id="delete-confirm"
                  type="text"
                  value={deleteDraft.confirm}
                  onChange={(e) =>
                    setDeleteDraft((d) => ({ ...d, confirm: e.target.value }))
                  }
                  className="input w-full font-mono"
                  placeholder="DELETE"
                  autoComplete="off"
                  spellCheck={false}
                  required
                  autoFocus
                  disabled={deleteMutation.isPending}
                />
              </div>

              <div>
                <label
                  htmlFor="delete-password"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Tu contraseña actual
                </label>
                <input
                  id="delete-password"
                  type="password"
                  value={deleteDraft.password}
                  onChange={(e) =>
                    setDeleteDraft((d) => ({ ...d, password: e.target.value }))
                  }
                  className="input w-full"
                  autoComplete="current-password"
                  minLength={8}
                  required
                  disabled={deleteMutation.isPending}
                />
              </div>

              <div>
                <label
                  htmlFor="delete-totp"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Código TOTP (6 dígitos)
                </label>
                <input
                  id="delete-totp"
                  type="text"
                  value={deleteDraft.totpCode}
                  onChange={(e) =>
                    setDeleteDraft((d) => ({
                      ...d,
                      totpCode: e.target.value.replace(/\D/g, '').slice(0, 6),
                    }))
                  }
                  className="input w-full text-center text-2xl tracking-widest"
                  placeholder="000000"
                  maxLength={6}
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  autoComplete="one-time-code"
                  required
                  disabled={deleteMutation.isPending}
                />
              </div>

              {deleteError && (
                <div className="p-3 rounded-lg bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800 text-sm">
                  {deleteError}
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeDeleteModal}
                  disabled={deleteMutation.isPending}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={
                    deleteMutation.isPending ||
                    deleteDraft.confirm !== 'DELETE' ||
                    deleteDraft.password.length < 8 ||
                    deleteDraft.totpCode.length !== 6
                  }
                  className="px-4 py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {deleteMutation.isPending ? 'Eliminando...' : 'Eliminar integración'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
